import { Controller, useFieldArray } from 'react-hook-form';
import { Input } from '@monksflow/monks-ui';
import { RadioIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getPlatformConfig } from '@/shared/constants';
import { OptimizedImage } from '@/shared/ui';
import { FormSectionV2, PersonaFieldLabel } from '../../PersonaFormSectionsV2';
import { useEditPersonaFormContext } from '../hooks/useEditPersonaFormContext';

const EM_DASH = '—';

export function ChannelsSectionV2() {
  const { t } = useTranslation('audiences');
  const { control } = useEditPersonaFormContext();
  const { fields } = useFieldArray({ control, name: 'channels' });

  return (
    <FormSectionV2
      icon={<RadioIcon className="size-5" />}
      title={t('audiences.channelsSection.channels')}
    >
      <div className="flex flex-col gap-sm">
        <div className="flex items-center gap-sm">
          <div className="w-8" aria-hidden />
          <div className="w-40">
            <PersonaFieldLabel>{t('audiences.channelsSection.channel')}</PersonaFieldLabel>
          </div>
          <div className="w-16">
            <PersonaFieldLabel>{t('audiences.channelsSection.share')}</PersonaFieldLabel>
          </div>
          <div className="flex-1">
            <PersonaFieldLabel required>
              {t('audiences.channelsSection.description')}
            </PersonaFieldLabel>
          </div>
        </div>

        {fields.map((channel, index) => {
          const platformImage = getPlatformConfig(channel.platformName)?.image;
          // A channel the persona does not use has no share to describe.
          const hasShare = channel.sharePercent > 0;

          return (
            <div key={channel.id} className="flex items-center gap-sm">
              <div className="flex w-8 justify-center">
                {platformImage && (
                  <OptimizedImage
                    src={platformImage}
                    alt={channel.platformName}
                    width={24}
                    height={24}
                  />
                )}
              </div>
              <span className="w-40 text-sm text-foreground">{channel.platformName}</span>
              <span className="w-16 text-sm text-foreground">{channel.sharePercent}</span>
              <div className="flex-1">
                {hasShare ? (
                  <Controller
                    name={`channels.${index}.description`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <Input
                          {...field}
                          placeholder={t('audiences.channelsSection.description')}
                          aria-label={channel.platformName}
                          aria-invalid={Boolean(error) || undefined}
                        />
                        {error?.message && (
                          <p className="mt-3xs text-xs text-destructive">{error.message}</p>
                        )}
                      </>
                    )}
                  />
                ) : (
                  <span className="text-sm text-muted-foreground">{EM_DASH}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </FormSectionV2>
  );
}

ChannelsSectionV2.displayName = 'ChannelsSectionV2';
